import { useState, useEffect, useRef, useMemo } from "react";
import { useCarousel } from "../hooks/useCarousel";
import { useApp } from "../context/AppContext";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { usePointsConfig } from "../context/PointsConfigContext";
import { useWishlist } from "../context/WishlistContext";
import { useDailyDeals } from "../hooks/useDailyDeals";
import { useBouquets } from "../hooks/useBouquets";
import { imgUrl, pexelsImg } from "../lib/imageUrl";
import ExploreBouquets from "../components/ui/ExploreBouquets";
import {
  Leaf,
  Truck,
  MapPin,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Heart,
  Star,
  Sparkles,
  Check,
  Gift,
  Users,
  Coins,
  ArrowRight,
} from "lucide-react";

import herobanner1 from "../assets/homebg-1.webp";
import herobanner2 from "../assets/homebg-2.webp";
import herobanner3 from "../assets/homebg-3.webp";
import herobannerMob1 from "../assets/homebg-mob-1.webp";
import herobannerMob2 from "../assets/homebg-mob-2.webp";
import herobannerMob3 from "../assets/homebg-mob-3.webp";
import awardBadge from "../assets/awards/award-badge.png";
import awardBannerLandscape from "../assets/awards/award-banner-landscape.png";

// theme: "dark" = dark text on light bg | "light" = white text on dark bg | "pink" = deep rose text on pinkish bg
const heroSlides = [
  {
    theme: "dark",
    eyebrow: "Best Florist 2026 - Moonee Valley",
    headline: "Melbourne's\nFavourite Florist",
    sub: "Handcrafted daily in our Travancore studio. Same-day delivery across Melbourne.",
    pills: ["⭐ 5-Star Rated", "2,500+ Happy Customers", "100% Fresh Blooms"],
    cta: "Shop Now",
    align: "left",
    image: herobanner1,
    imageMobile: herobannerMob1,
  },
  {
    theme: "light",
    eyebrow: "Go local",
    headline: "Market Fresh Flowers",
    sub: "Love in every petal — arranged this morning in our Travancore studio, just for you.",
    pills: ["Same-Day Delivery", "Eco-Conscious", "Local Melbourne Growers"],
    cta: "Browse Arrangements",
    align: "right",
    image: herobanner2,
    imageMobile: herobannerMob2,
  },
  {
    theme: "pink",
    eyebrow: "For Every Occasion",
    headline: "Birthday, Love,\nSympathy & More",
    sub: "Whatever the moment calls for, we craft it with heart. Free gift message with every order.",
    pills: [
      "Free Gift Message",
      "Occasions for Everyone",
      "Happiness Guaranteed",
    ],
    cta: "Explore Collections",
    align: "left",
    image: herobanner3,
    imageMobile: herobannerMob3,
  },
];

// Category strings must match occasion names in DB (case-insensitive compare in ShopPage)
// 4 main image cards (big one first)
const collectionCards = [
  {
    label: "Birthday Joy",
    category: "Birthday",
    fallbackFrom: 30,
    fallbackImage: "https://images.pexels.com/photos/931164/pexels-photo-931164.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    label: "Date Night",
    category: "Date Night",
    fallbackFrom: 45,
    fallbackImage: "https://images.pexels.com/photos/29480511/pexels-photo-29480511.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    label: "Gentle Sympathy",
    category: "Sympathy",
    fallbackFrom: 30,
    fallbackImage: "https://images.pexels.com/photos/7666497/pexels-photo-7666497.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
  {
    label: "Just Because",
    category: "Just Because",
    fallbackFrom: 30,
    fallbackImage: "https://images.pexels.com/photos/5743922/pexels-photo-5743922.jpeg?auto=compress&cs=tinysrgb&w=600",
  },
];

// Desktop row 2: 2 image cards + 1 browse-all tile
// Mobile: Get Well Soon image card + Thank You text tile + browse-all tile
const collectionExtra = [
  { label: "Get Well Soon", category: "Get Well Soon", fallbackFrom: 30, image: true },
  { label: "Thank You", category: "Thank You", fallbackFrom: 30, image: true },
];


const testimonials = [
  {
    text: "The most stunning arrangement I've ever received. My wife was in tears of joy.",
    author: "James T., Ascot Vale",
  },
  {
    text: "Ordered same-day for mum's birthday and they arrived perfectly fresh.",
    author: "Sarah M., Travancore",
  },
  {
    text: "We use Happy Day for all our corporate events. Impeccable taste every single time.",
    author: "Priya K., Melbourne CBD",
  },
];

const trustItems = [
  { icon: <Leaf className="w-4 h-4" />, label: "100% Fresh" },
  { icon: <Truck className="w-4 h-4" />, label: "Same-Day Delivery" },
  { icon: <MapPin className="w-4 h-4" />, label: "Melbourne Local" },
  { icon: <ShieldCheck className="w-4 h-4" />, label: "Secure Checkout" },
  { icon: <Heart className="w-4 h-4" />, label: "Happiness Guaranteed" },
  { icon: <Sparkles className="w-4 h-4" />, label: "Handcrafted Daily" },
  { icon: <Star className="w-4 h-4" />, label: "5-Star Florist" },
];

const process = [
  {
    step: "01",
    title: "Choose Your Blooms",
    desc: "Browse our curated collection and pick what speaks to your heart.",
  },
  {
    step: "02",
    title: "We Handcraft It",
    desc: "Our florists arrange your bouquet fresh in our Travancore studio.",
  },
  {
    step: "03",
    title: "Delivered with Love",
    desc: "Same-day Melbourne delivery, wrapped beautifully and on time.",
  },
];

// Brand-consistent badge colour map — no off-brand colours
const BADGE_STYLES = {
  "Best Seller": "bg-brand-forest-600",
  "Most Gifted": "bg-brand-crimson-500",
  New: "bg-amber-500",
  Premium: "bg-brand-forest-700",
  "Fan Fave": "bg-brand-crimson-400",
  Wedding: "bg-brand-forest-500",
  Trending: "bg-brand-crimson-500",
  Seasonal: "bg-amber-600",
  Rare: "bg-brand-forest-600",
  Limited: "bg-brand-crimson-600",
  Luxe: "bg-brand-forest-700",
  Classic: "bg-brand-forest-500",
};
const badgeCls = (b) => BADGE_STYLES[b] || "bg-brand-forest-500";

function CarouselCard({ p, onNavigate, didDrag }) {
  const { addToCart } = useCart();
  const { setModalProduct, showToast } = useApp();
  const { user } = useAuth();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const [justAdded, setJustAdded] = useState(false);
  const [localWished, setLocalWished] = useState(false);

  // Real DB product when id is present
  const isReal = Boolean(p.id);

  const handleClick = () => {
    if (didDrag.current) return;
    if (isReal) setModalProduct(p);
    else onNavigate("shop");
  };

  const handleAdd = (e) => {
    e.stopPropagation();
    if (isReal) {
      addToCart(p);
      showToast({ name: p.name, image: p.images?.[0] ?? p.image });
      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 1600);
    } else {
      onNavigate("shop");
    }
  };

  const handleWishlist = (e) => {
    e.stopPropagation();
    if (isReal) {
      if (user) {
        toggleWishlist(p.id, {
          id: p.id,
          bouquet_name: p.name,
          image_url: p.images?.[0],
          image_urls: p.images,
          price: p.price,
          size_prices: p.size_prices,
        });
      } else {
        setModalProduct(p); // prompt sign-in via modal
      }
    } else {
      setLocalWished((w) => !w);
    }
  };

  const wishlisted = isReal && user ? isWishlisted(p.id) : localWished;
  const rating = p.rating ?? 4.9;

  return (
    <div
      onClick={handleClick}
      className="group cursor-pointer bg-brand-cream-50 rounded-2xl overflow-hidden border border-brand-cream-200
                 shadow-[0_2px_12px_rgba(0,0,0,0.06)] hover:shadow-[0_16px_40px_rgba(0,0,0,0.13)]
                 hover:-translate-y-1 transition-all duration-400 flex flex-col shrink-0"
      style={{ width: "min(72vw, 220px)", scrollSnapAlign: "start" }}
    >
      {/* Image — portrait 3:4 */}
      <div className="relative overflow-hidden aspect-3/4">
        <img
          src={p.image}
          alt={p.name}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />

        {/* Badge */}
        <span
          className={`absolute top-3 left-3 ${badgeCls(p.badge)} text-white font-body text-[10px] px-2.5 py-1 rounded-full font-semibold shadow-md`}
        >
          {p.badge}
        </span>

        {/* Wishlist */}
        <button
          onClick={handleWishlist}
          aria-label={
            wishlisted
              ? `Remove ${p.name} from favourites`
              : `Save ${p.name} to favourites`
          }
          className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center shadow-md transition-all duration-300
            ${wishlisted ? "bg-brand-crimson-500 scale-110" : "bg-brand-cream-50/90 hover:bg-brand-cream-50"}`}
        >
          <Heart
            className={`w-3.5 h-3.5 transition-all ${wishlisted ? "fill-white text-white" : "text-brand-forest-400"}`}
          />
        </button>

        {/* Bottom: price + social proof */}
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
          <div className="bg-brand-cream-50/95 backdrop-blur-sm rounded-xl px-2.5 py-1 shadow-sm">
            <span className="font-body text-[#5C3D2E] text-base leading-none">
              $<span className="font-display">{p.price}</span>{" "}
              <span className="text-[9px] opacity-60">AUD</span>
            </span>
          </div>
          <span className="font-body text-white/85 text-[10px] drop-shadow leading-tight text-right max-w-[80px]">
            {p.orders}
          </span>
        </div>
      </div>

      {/* Card body */}
      <div className="px-3.5 pt-3 pb-3.5 flex flex-col gap-2.5">
        {/* Stars */}
        <div className="flex items-center gap-0.5">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`w-2.5 h-2.5 ${i < Math.floor(rating) ? "fill-amber-400 text-amber-400" : "fill-stone-100 text-stone-100"}`}
            />
          ))}
          <span className="font-body text-[10px] text-brand-forest-400 ml-1">
            {rating}
          </span>
        </div>

        {/* Name + desc */}
        <div className="-mt-0.5">
          <h3 className="font-display text-brand-forest-800 text-[0.85rem] leading-snug line-clamp-1">
            {p.name}
          </h3>
          <p className="font-elegant italic text-brand-forest-400 text-[11px] mt-0.5 line-clamp-1">
            {p.desc}
          </p>
        </div>

        {/* Add to Cart */}
        <button
          onClick={handleAdd}
          className={`w-full font-body text-[11px] font-semibold py-2.5 rounded-xl transition-all duration-300 shadow-sm
            ${
              justAdded
                ? "bg-brand-forest-600 text-white"
                : "bg-brand-forest-500 hover:bg-brand-forest-600 active:bg-brand-forest-700 text-white"
            }`}
        >
          {justAdded ? "Added ✓" : "Add to Cart"}
        </button>
      </div>
    </div>
  );
}

// How many full cards visible at once (approx) for segment dots
const CARDS_VISIBLE = 5;

function ProductCarousel({ products, onNavigate }) {
  const trackRef = useRef(null);
  const didDrag = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const velocity = useRef(0);
  const lastX = useRef(0);
  const animFrame = useRef(null);
  const isDragging = useRef(false);

  const totalSegments = Math.ceil(products.length / CARDS_VISIBLE);
  const [activeSegment, setActiveSegment] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const syncState = () => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft < max - 8);
    setActiveSegment(Math.round((el.scrollLeft / max) * (totalSegments - 1)));
  };

  useEffect(() => {
    syncState();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", syncState, { passive: true });
    return () => el.removeEventListener("scroll", syncState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const slide = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
    setTimeout(syncState, 500);
  };

  const goToSegment = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    el.scrollTo({ left: (i / (totalSegments - 1)) * max, behavior: "smooth" });
    setTimeout(syncState, 500);
  };

  // Mouse drag
  const onMouseDown = (e) => {
    const el = trackRef.current;
    if (!el) return;
    isDragging.current = true;
    didDrag.current = false;
    dragStartX.current = e.pageX;
    dragScrollLeft.current = el.scrollLeft;
    lastX.current = e.pageX;
    velocity.current = 0;
    cancelAnimationFrame(animFrame.current);
    el.style.cursor = "grabbing";
  };
  const onMouseMove = (e) => {
    if (!isDragging.current) return;
    const el = trackRef.current;
    if (!el) return;
    const dx = e.pageX - dragStartX.current;
    if (Math.abs(dx) > 4) didDrag.current = true;
    velocity.current = e.pageX - lastX.current;
    lastX.current = e.pageX;
    el.scrollLeft = dragScrollLeft.current - dx * 1.4;
    syncState();
  };
  const onMouseUp = () => {
    if (!isDragging.current) return;
    const el = trackRef.current;
    if (!el) return;
    isDragging.current = false;
    el.style.cursor = "grab";
    let vel = velocity.current * 7;
    const coast = () => {
      if (Math.abs(vel) < 0.5) {
        syncState();
        return;
      }
      el.scrollLeft -= vel;
      vel *= 0.91;
      animFrame.current = requestAnimationFrame(coast);
    };
    animFrame.current = requestAnimationFrame(coast);
  };

  // Touch
  const onTouchStart = (e) => {
    const el = trackRef.current;
    if (!el) return;
    didDrag.current = false;
    dragStartX.current = e.touches[0].clientX;
    dragScrollLeft.current = el.scrollLeft;
    lastX.current = e.touches[0].clientX;
    velocity.current = 0;
  };
  const onTouchMove = (e) => {
    const el = trackRef.current;
    if (!el) return;
    const dx = dragStartX.current - e.touches[0].clientX;
    if (Math.abs(dx) > 4) didDrag.current = true;
    velocity.current = e.touches[0].clientX - lastX.current;
    lastX.current = e.touches[0].clientX;
    el.scrollLeft = dragScrollLeft.current + dx;
    syncState();
  };

  return (
    <div className="relative">
      {/* Prev arrow — desktop */}
      <button
        onClick={() => slide(-1)}
        aria-label="Previous products"
        className={`hidden md:flex absolute -left-6 top-[45%] -translate-y-1/2 z-20
          w-12 h-12 bg-brand-cream-50 border border-brand-cream-300 rounded-full shadow-lg
          items-center justify-center transition-all duration-300
          ${canPrev ? "opacity-100 hover:bg-brand-cream-100 hover:scale-110 hover:shadow-xl" : "opacity-0 pointer-events-none"}`}
      >
        <ChevronLeft className="w-5 h-5 text-brand-forest-600" />
      </button>

      {/* Next arrow — desktop */}
      <button
        onClick={() => slide(1)}
        aria-label="Next products"
        className={`hidden md:flex absolute -right-6 top-[45%] -translate-y-1/2 z-20
          w-12 h-12 bg-brand-cream-50 border border-brand-cream-300 rounded-full shadow-lg
          items-center justify-center transition-all duration-300
          ${canNext ? "opacity-100 hover:bg-brand-cream-100 hover:scale-110 hover:shadow-xl" : "opacity-0 pointer-events-none"}`}
      >
        <ChevronRight className="w-5 h-5 text-brand-forest-600" />
      </button>

      {/* Edge fades */}
      {/* <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-brand-cream-50 to-transparent z-10 pointer-events-none rounded-l-2xl" />
      <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-brand-cream-50 to-transparent z-10 pointer-events-none rounded-r-2xl" /> */}

      {/* Track */}
      <div
        ref={trackRef}
        className="flex gap-4 overflow-x-auto pb-2"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          scrollSnapType: "x mandatory",
          cursor: "grab",
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
      >
        {products.map((p) => (
          <CarouselCard
            key={p.name}
            p={p}
            onNavigate={onNavigate}
            didDrag={didDrag}
          />
        ))}
        {/* Trailing spacer so last card peeks cleanly */}
        <div className="shrink-0 w-2" aria-hidden="true" />
      </div>

      {/* Segment dots + arrow tray */}
      <div className="flex items-center justify-center gap-3 mt-5">
        {/* Mobile prev */}
        <button
          onClick={() => slide(-1)}
          aria-label="Previous products"
          className={`md:hidden w-8 h-8 rounded-full border border-brand-cream-300 bg-brand-cream-50
            flex items-center justify-center shadow-sm transition-all duration-200
            ${canPrev ? "opacity-100" : "opacity-30 pointer-events-none"}`}
        >
          <ChevronLeft className="w-4 h-4 text-brand-forest-500" />
        </button>

        {/* Segment dots */}
        <div className="flex items-center gap-1.5">
          {Array.from({ length: totalSegments }).map((_, i) => (
            <button
              key={i}
              onClick={() => goToSegment(i)}
              aria-label={`Go to product group ${i + 1}`}
              className={`rounded-full transition-all duration-300
                ${
                  i === activeSegment
                    ? "w-6 h-2 bg-brand-crimson-500"
                    : "w-2 h-2 bg-brand-cream-300 hover:bg-brand-cream-400"
                }`}
            />
          ))}
        </div>

        {/* Mobile next */}
        <button
          onClick={() => slide(1)}
          aria-label="Next products"
          className={`md:hidden w-8 h-8 rounded-full border border-brand-cream-300 bg-brand-cream-50
            flex items-center justify-center shadow-sm transition-all duration-200
            ${canNext ? "opacity-100" : "opacity-30 pointer-events-none"}`}
        >
          <ChevronRight className="w-4 h-4 text-brand-forest-500" />
        </button>
      </div>
    </div>
  );
}

function ImageCard({ c, tall = false, navigate }) {
  return (
    <div
      onClick={() => navigate("shop", c.category)}
      className={`group cursor-pointer relative rounded-3xl overflow-hidden shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-500 ${tall ? "row-span-2" : ""}`}
    >
      <img
        src={pexelsImg(c.image, { width: tall ? 700 : 500 })}
        alt={c.label}
        loading="lazy"
        decoding="async"
        className={`w-full object-cover group-hover:scale-105 transition-transform duration-700 ${tall ? "h-full min-h-[180px]" : "h-44"}`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-forest-700/70 via-transparent to-transparent flex flex-col justify-end p-4">
        <p className="font-display text-white text-base leading-tight">{c.label}</p>
      </div>
    </div>
  );
}

function BrowseAllTile({ compact = false, navigate }) {
  return (
    <div
      onClick={() => navigate("shop")}
      className={`group cursor-pointer rounded-3xl border border-[--hdf-brown-200] bg-white hover:bg-brand-forest-500 hover:border-brand-forest-500 transition-all duration-300 flex ${compact ? "flex-row items-center justify-between px-4 py-3" : "flex-col justify-center items-start px-5 py-4"} shadow-sm hover:shadow-md`}
    >
      {compact ? (
        <>
          <p className="font-display text-brand-forest-700 group-hover:text-white text-sm transition-colors duration-300">Browse All Occasions</p>
          <ArrowRight className="w-4 h-4 text-brand-crimson-400 group-hover:text-white transition-colors duration-300 shrink-0" />
        </>
      ) : (
        <>
          <p className="font-display text-brand-forest-700 group-hover:text-white text-base leading-tight transition-colors duration-300">Browse All Occasions</p>
          <p className="font-body text-brand-forest-400 group-hover:text-white/70 text-xs mt-1 transition-colors duration-300">Every flower, every feeling</p>
          <ArrowRight className="w-4 h-4 mt-2 text-brand-crimson-400 group-hover:text-white transition-colors duration-300" />
        </>
      )}
    </div>
  );
}

export default function HomePage() {
  const { navigate, setModalProduct } = useApp();
  const { user, profile, setAuthModalOpen, setAccountPanelOpen } = useAuth();
  const { POINTS_REFERRAL, POINTS_SIGNUP_BONUS, POINTS_PER_DOLLAR } = usePointsConfig();
  const [referralCopied, setReferralCopied] = useState(false);
  const { deals, loading: dealsLoading } = useDailyDeals();
  const { bouquets, loading: bouquetsLoading } = useBouquets();
  const featuredArrangement = bouquets.find((b) => b.is_featured) ?? null;
  const hero = useCarousel(3, 5500);

  // Google reviews — fetched from edge function (cached 24h server-side)
  const [reviews, setReviews] = useState([]);
  const [reviewsMeta, setReviewsMeta] = useState({
    rating: null,
    total_count: null,
  });
  const [reviewsLoading, setReviewsLoading] = useState(true);
  useEffect(() => {
    // Reviews section is far below the fold. Defer the fetch until the main
    // thread is idle (or 2s elapse) so it doesn't compete with LCP work.
    const runFetch = () =>
      import("../lib/supabase").then(({ default: supabase }) => {
        supabase.functions
          .invoke("get-google-reviews")
          .then(({ data }) => {
            if (data?.reviews?.length) {
              setReviews(
                data.reviews.map((r) => ({
                  author: r.author_name ?? "Google Reviewer",
                  text: r.text ?? "",
                  rating: r.rating ?? 5,
                  avatar: r.profile_photo_url || null,
                  relative: r.relative_time_description ?? null,
                })),
              );
              setReviewsMeta({
                rating: data.rating,
                total_count: data.total_count,
              });
            } else {
              setReviews(
                testimonials.map((t) => ({
                  author: t.author,
                  text: t.text,
                  rating: 5,
                  avatar: null,
                  relative: null,
                })),
              );
            }
          })
          .catch(() => {
            setReviews(
              testimonials.map((t) => ({
                author: t.author,
                text: t.text,
                rating: 5,
                avatar: null,
                relative: null,
              })),
            );
          })
          .finally(() => setReviewsLoading(false));
      });

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const id = window.requestIdleCallback(runFetch, { timeout: 2000 });
      return () => window.cancelIdleCallback?.(id);
    }
    const t = setTimeout(runFetch, 1500);
    return () => clearTimeout(t);
  }, []);

  const testi = useCarousel(Math.max(reviews.length, 1), 6000);
  const slide = heroSlides[hero.index];

  const featuredDeal = deals[0] ?? null;

  // Countdown to deal's valid_till (falls back to midnight if no deal)
  const getDealSeconds = () => {
    const target = featuredDeal?.validTill
      ? new Date(featuredDeal.validTill)
      : (() => {
          const m = new Date();
          m.setHours(24, 0, 0, 0);
          return m;
        })();
    return Math.max(0, Math.floor((target - Date.now()) / 1000));
  };
  const totalDealSecs = useMemo(getDealSeconds, [featuredDeal?.validTill]); // eslint-disable-line react-hooks/exhaustive-deps
  const [timeLeft, setTimeLeft] = useState(getDealSeconds);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((t) => Math.max(0, t - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const hrs = String(Math.floor(timeLeft / 3600)).padStart(2, "0");
  const mins = String(Math.floor((timeLeft % 3600) / 60)).padStart(2, "0");
  const secs = String(timeLeft % 60).padStart(2, "0");

  // Hero touch swipe
  const heroTouchX = useRef(null);
  const onHeroTouchStart = (e) => {
    heroTouchX.current = e.touches[0].clientX;
  };
  const onHeroTouchEnd = (e) => {
    if (heroTouchX.current === null) return;
    const dx = e.changedTouches[0].clientX - heroTouchX.current;
    if (Math.abs(dx) > 50) dx < 0 ? hero.next() : hero.prev();
    heroTouchX.current = null;
  };

  return (
    <div className="font-body bg-brand-cream-50 overflow-x-hidden">
      {/* ── HERO — single crossfade carousel on all breakpoints ── */}
      {/* Mobile: fills viewport minus navbar (64px) and trust bar (44px).
          Desktop: also subtracts the announcement bar (56px). */}
      <section
        className="relative w-full overflow-hidden
          h-[calc(100dvh-64px-44px)]
          md:h-[calc(100dvh-64px-44px-56px)]"
        onTouchStart={onHeroTouchStart}
        onTouchEnd={onHeroTouchEnd}
      >
        {/* Background images */}
        {heroSlides.map((s, i) => (
          <picture
            key={i}
            className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${i === hero.index ? "opacity-100" : "opacity-0"}`}
          >
            <source media="(min-width: 768px)" srcSet={s.image} />
            <img
              src={s.imageMobile ?? s.image}
              alt={i === 0 ? s.headline : ""}
              aria-hidden={i !== 0}
              width="1920"
              height="960"
              fetchPriority={i === 0 ? "high" : "low"}
              loading={i === 0 ? "eager" : "lazy"}
              decoding={i === 0 ? "sync" : "async"}
              className="w-full h-full object-cover"
              style={{ objectPosition: "center center" }}
            />
          </picture>
        ))}

        {/* Content */}
        {(() => {
          const t = slide.theme ?? "light";

          // Desktop colour tokens (unchanged)
          const eyebrowCls =
            t === "dark"
              ? "text-brand-forest-700 bg-white/75 border-brand-forest-200/70 shadow-sm"
              : t === "pink"
                ? "text-[#6b1530] bg-white/60 border-[#d4859a]/60 shadow-sm"
                : "text-white bg-white/18 border-white/30 backdrop-blur-md";
          const h1Cls =
            t === "dark"
              ? "text-brand-forest-900 drop-shadow-[0_2px_8px_rgba(255,255,255,0.7)]"
              : t === "pink"
                ? "text-[#4a0e20] drop-shadow-[0_2px_10px_rgba(255,255,255,0.55)]"
                : "text-white drop-shadow-[0_3px_18px_rgba(0,0,0,0.9)]";
          const subCls =
            t === "dark"
              ? "text-brand-forest-800"
              : t === "pink"
                ? "text-[#6b1530]"
                : "text-white/95 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]";
          const pillCls =
            t === "dark"
              ? "bg-brand-forest-700 text-white shadow-sm"
              : t === "pink"
                ? "bg-[#6b1530]/85 text-white shadow-sm"
                : "bg-white/22 backdrop-blur-sm text-white border border-white/35";
          const btnCls =
            t === "dark"
              ? "bg-brand-forest-600 hover:bg-brand-forest-700 text-white shadow-xl hover:shadow-2xl hover:scale-[1.03]"
              : t === "pink"
                ? "bg-[#880d1e] hover:bg-[#6e0b18] text-white shadow-xl hover:shadow-2xl hover:scale-[1.03]"
                : "bg-white hover:bg-brand-cream-50 text-brand-forest-800 shadow-xl hover:shadow-2xl hover:scale-[1.03]";
          const trustCls =
            t === "dark"
              ? "text-brand-forest-600"
              : t === "pink"
                ? "text-[#7a1c35]"
                : "text-white/80 drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)]";
          const dividerCls =
            t === "dark"
              ? "bg-brand-forest-300"
              : t === "pink"
                ? "bg-[#c08090]"
                : "bg-white/40";

          // Mobile overrides for slide 2 ("light" theme) — dark text since the
          // mobile image is lighter and white text won't read well without a scrim.
          const mobEyebrowCls =
            t === "light"
              ? "text-brand-forest-700 bg-white/75 border-brand-forest-200/70 shadow-sm"
              : eyebrowCls;
          const mobH1Cls =
            t === "light"
              ? "text-brand-forest-900 drop-shadow-[0_2px_8px_rgba(255,255,255,0.7)]"
              : h1Cls;
          const mobSubCls = t === "light" ? "text-brand-forest-800" : subCls;
          const mobPillCls =
            t === "light"
              ? "bg-brand-forest-700 text-white shadow-sm"
              : pillCls;
          const mobBtnCls =
            t === "light"
              ? "bg-brand-forest-600 text-white shadow-xl active:scale-95"
              : btnCls;

          // Shared layout wrapper props
          const wrapperBase =
            "z-30 absolute flex flex-col top-1/2 -translate-y-1/2";

          // Shared inner content renderer — accepts the colour classes to use
          const renderContent = ({ ec, h1c, sc, pc, bc, tc, dc, isMobile }) => (
            <>
              {/* Eyebrow */}
              <div
                className={`inline-flex items-center gap-2.5 rounded-full border px-4 py-2.5 ${ec}`}
              >
                <Star className="w-3.5 h-3.5 fill-current opacity-80 shrink-0" />
                <span className="font-elegant text-[12px] tracking-[0.22em] uppercase leading-none">
                  {slide.eyebrow}
                </span>
              </div>

              {/* Headline */}
              <h1
                className={`font-display leading-[1.07] ${isMobile ? "text-[2.5rem] sm:text-[3rem]" : "text-[4.5rem]"} ${h1c}`}
              >
                {slide.headline.split("\n").map((line, i, arr) => (
                  <span key={i}>
                    {line}
                    {i < arr.length - 1 && <br />}
                  </span>
                ))}
              </h1>

              {/* Subline */}
              <p
                className={`font-elegant italic leading-relaxed ${isMobile ? "text-lg sm:text-xl max-w-[22rem] sm:max-w-sm" : "text-2xl max-w-[28rem]"} ${sc}`}
              >
                {slide.sub}
              </p>

              {/* Pills */}
              <div
                className={`flex flex-wrap gap-2 ${isMobile ? "justify-center" : slide.align === "right" ? "justify-end" : "justify-start"}`}
              >
                {slide.pills.map((pill, pi) => (
                  <span
                    key={pill}
                    className={`font-body font-semibold rounded-full text-xs px-3.5 py-2 ${pc} ${isMobile && pi === 2 ? "hidden" : ""}`}
                  >
                    {pill}
                  </span>
                ))}
              </div>

              {/* CTA */}
              <div
                className={`flex flex-col gap-2.5 ${isMobile ? "items-center" : slide.align === "right" ? "items-end" : "items-start"}`}
              >
                <button
                  onClick={() => navigate("shop")}
                  className={`inline-flex items-center gap-2.5 font-body font-bold rounded-full transition-all duration-300 tracking-wide active:scale-95 text-sm px-7 py-4 ${bc}`}
                >
                  {slide.cta}
                  <ArrowRight className="w-4 h-4" />
                </button>
                {/* Trust line — desktop only */}
                {!isMobile && (
                  <div
                    className={`flex items-center gap-2 font-body text-[11px] ${tc}`}
                  >
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, j) => (
                        <Star key={j} className="w-2.5 h-2.5 fill-current" />
                      ))}
                    </div>
                    <span className={`w-px h-3 ${dc}`} />
                    <span>5.0 · 2,500+ happy customers in Melbourne</span>
                  </div>
                )}
              </div>
            </>
          );

          return (
            <>
              {/* ── MOBILE content (uses mob* colour tokens) ── */}
              <div
                className={`${wrapperBase} md:hidden gap-4 inset-x-0 px-6 sm:px-10 items-center text-center`}
              >
                {renderContent({
                  ec: mobEyebrowCls,
                  h1c: mobH1Cls,
                  sc: mobSubCls,
                  pc: mobPillCls,
                  bc: mobBtnCls,
                  tc: trustCls,
                  dc: dividerCls,
                  isMobile: true,
                })}
              </div>

              {/* ── DESKTOP content (uses original theme tokens) ── */}
              <div
                className={`${wrapperBase} hidden md:flex gap-5 w-auto max-w-[540px]
                ${slide.align === "right" ? "right-16 left-auto items-end text-right" : "left-16 right-auto items-start text-left"}`}
              >
                {renderContent({
                  ec: eyebrowCls,
                  h1c: h1Cls,
                  sc: subCls,
                  pc: pillCls,
                  bc: btnCls,
                  tc: trustCls,
                  dc: dividerCls,
                  isMobile: false,
                })}
              </div>
            </>
          );
        })()}

        {/* Dots — centred horizontally on both mobile and desktop */}
        <div className="z-30 absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => hero.goTo(i)}
              className="p-2 flex items-center justify-center group"
            >
              <span
                className={`block h-[5px] rounded-full transition-all duration-500
                  ${
                    i === hero.index
                      ? "w-10 bg-white shadow-[0_0_8px_rgba(255,255,255,0.7)]"
                      : "w-[5px] bg-white/40 group-hover:bg-white/65"
                  }`}
              />
            </button>
          ))}
        </div>

        {/* Arrows — intentionally hidden
        <button
          onClick={hero.prev}
          className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-30
            w-9 h-9 md:w-11 md:h-11
            bg-white/15 hover:bg-white/35 backdrop-blur-sm border border-white/25
            rounded-full transition-all duration-200 flex items-center justify-center
            hover:scale-110 shadow-lg"
        >
          <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 text-white" />
        </button>
        <button
          onClick={hero.next}
          className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-30
            w-9 h-9 md:w-11 md:h-11
            bg-white/15 hover:bg-white/35 backdrop-blur-sm border border-white/25
            rounded-full transition-all duration-200 flex items-center justify-center
            hover:scale-110 shadow-lg"
        >
          <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-white" />
        </button> */}
      </section>

      {/* ── TRUST BAR ── */}
      <div
        className="w-full border-y"
        style={{
          backgroundColor: "#f5ede1",
          borderColor: "var(--hdf-brown-200)",
        }}
      >
        {/* mobile/tablet: single scrollable row; lg+: centered flex row */}
        <div className="flex lg:justify-center overflow-x-auto px-4 py-2 gap-0 scrollbar-none items-center">
          {trustItems.map((t) => (
            <div
              key={t.label}
              className="flex items-center gap-2 px-4 py-1 shrink-0"
              style={{ borderRight: "1px solid var(--hdf-brown-200)" }}
            >
              <span style={{ color: "var(--hdf-accent)" }} className="shrink-0">
                {t.icon}
              </span>
              <span
                className="font-body text-[11.5px] font-semibold whitespace-nowrap"
                style={{ color: "var(--hdf-accent)" }}
              >
                {t.label}
              </span>
            </div>
          ))}
          {/* Award badge */}
          <div className="flex items-center gap-2.5 px-4 py-1 shrink-0">
            <img
              src={awardBadge}
              alt="Quality Business Awards 2026 Winner"
              className="h-9 w-auto"
            />
            <span
              className="font-body text-[11.5px] font-semibold whitespace-nowrap"
              style={{ color: "var(--hdf-accent)" }}
            >
              Best Florist 2026
            </span>
          </div>
        </div>
      </div>

      {/* ── BROWSE BUTTON — mobile only, separate from vine ── */}
      <div className="md:hidden flex justify-center pt-5 pb-6">
        <button
          onClick={() => navigate("shop")}
          className="flex items-center gap-2 bg-brand-forest-500 hover:bg-brand-forest-600 text-white font-body text-sm px-10 py-3.5 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 tracking-wider"
        >
          <span>Order something special Today!</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17 8l4 4m0 0l-4 4m4-4H3"
            />
          </svg>
        </button>
      </div>

      {/* ── EXPLORE BY OCCASION + PRICE ── */}
      <ExploreBouquets
        bouquets={bouquets}
        loading={bouquetsLoading}
        onNavigate={navigate}
      />

      {/* ── TOP SELLERS ── */}
      <section className="py-12 md:py-20 bg-brand-cream-50">
        <div className="max-w-6xl mx-auto px-4">

          {/* ── DAILY DEAL ── */}
          {dealsLoading ? (
            <div className="rounded-[2rem] bg-brand-cream-100 animate-pulse h-80 mb-10" />
          ) : featuredDeal ? (
            <div
              onClick={() => setModalProduct(featuredDeal)}
              className="group cursor-pointer rounded-[2rem] overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 mb-10 bg-brand-cream-50 flex flex-col md:flex-row border border-brand-cream-200"
            >
              {/* Image side */}
              <div className="relative md:w-[55%] h-52 md:h-auto md:min-h-[380px] overflow-hidden flex-shrink-0 self-stretch">
                <img
                  src={featuredDeal.image}
                  alt={featuredDeal.name}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 absolute inset-0"
                />
                {/* Subtle vignette for readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent" />

                {/* Live badge */}
                <div className="absolute top-5 left-5">
                  <span className="relative flex items-center gap-2 bg-brand-crimson-500 text-white font-body text-xs px-4 py-2 rounded-full font-semibold shadow-lg">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <span className="w-1.5 h-1.5 rounded-full bg-white/90 relative ml-0.5" />
                    {featuredDeal.isDeal
                      ? "Today's Daily Deal"
                      : "Today's Design"}
                  </span>
                </div>

                {/* Price chip */}
                <div className="absolute bottom-5 left-5 bg-brand-cream-50/95 backdrop-blur-sm rounded-2xl px-4 py-2.5 shadow-lg flex items-baseline gap-2">
                  <span className="font-display text-brand-crimson-500 text-2xl">
                    ${featuredDeal.price}
                  </span>
                  {featuredDeal.isDeal && (
                    <>
                      <span className="font-body text-brand-forest-300 text-sm line-through">
                        ${featuredDeal.originalPrice}
                      </span>
                      <span className="bg-brand-crimson-500 text-white font-body text-[10px] px-2 py-0.5 rounded-full font-bold ml-1">
                        SAVE ${featuredDeal.originalPrice - featuredDeal.price}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Content side */}
              <div className="flex-1 p-7 md:p-10 flex flex-col justify-between gap-5">
                {/* Top — title + desc */}
                <div>
                  {featuredDeal.isDeal && (
                    <p className="font-body text-xs text-brand-crimson-500 tracking-[0.2em] uppercase mb-2 font-semibold">
                      Limited Time Offer
                    </p>
                  )}
                  <h3 className="font-display text-brand-forest-700 text-2xl md:text-4xl mb-2 leading-tight">
                    {featuredDeal.name}
                  </h3>
                  <p className="font-elegant italic text-brand-forest-400 text-base md:text-lg leading-relaxed">
                    {featuredDeal.desc}
                  </p>
                </div>

                {/* Countdown */}
                <div className="bg-brand-cream-100 border border-brand-cream-300 rounded-2xl px-4 py-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-body text-xs text-brand-forest-400 mb-1 tracking-wide uppercase">
                      {featuredDeal.isDeal ? "Deal Expires in:" : "Expires in:"}
                    </p>
                    <div className="flex items-end gap-1">
                      {[
                        { val: hrs, label: "hrs" },
                        { val: mins, label: "min" },
                        { val: secs, label: "sec" },
                      ].map(({ val, label }) => (
                        <div key={label} className="flex items-end gap-0.5">
                          <span className="font-mono text-brand-crimson-500 text-2xl font-bold leading-none">
                            {val}
                          </span>
                          <span className="font-body text-brand-forest-300 text-xs mb-0.5">
                            {label}
                          </span>
                          {label !== "sec" && (
                            <span className="font-mono text-brand-forest-300 text-xl leading-none mx-0.5">
                              :
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Depleting urgency bar */}
                  <div className="flex-1 max-w-[120px]">
                    <div className="flex justify-between mb-1.5">
                      <span className="font-body text-brand-forest-300 text-[10px]">
                        {featuredDeal.isDeal
                          ? "Deal expires"
                          : "Available until"}
                      </span>
                      <span className="font-body text-brand-crimson-500 text-[10px] font-semibold">
                        {featuredDeal.isDeal
                          ? `${Math.min(100, Math.round((timeLeft / (totalDealSecs || 1)) * 100))}%`
                          : featuredDeal.validTill
                            ? new Date(
                                featuredDeal.validTill,
                              ).toLocaleDateString("en-AU", {
                                day: "numeric",
                                month: "short",
                              })
                            : "—"}
                      </span>
                    </div>
                    <div className="h-1.5 bg-brand-cream-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-brand-crimson-400 to-brand-crimson-500 rounded-full transition-all duration-1000"
                        style={{
                          width: `${Math.min(100, (timeLeft / (totalDealSecs || 1)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Feature list */}
                <ul className="flex flex-col gap-2">
                  {[
                    "Hand-tied fresh this morning",
                    "Free gift message included",
                    "Same-day Melbourne delivery",
                  ].map((f) => (
                    <li
                      key={f}
                      className="flex items-center gap-2.5 font-body text-sm text-brand-forest-600"
                    >
                      <span className="w-5 h-5 rounded-full bg-brand-forest-500 flex items-center justify-center flex-shrink-0">
                        <Check
                          className="w-3 h-3 text-white"
                          strokeWidth={2.5}
                        />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                {/* Pinned review */}
                <div className="flex items-start gap-3 bg-brand-cream-100 border border-brand-cream-300 rounded-2xl px-4 py-3">
                  <div className="flex flex-shrink-0 mt-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-3 h-3 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>
                  {/* <p className="font-body text-brand-forest-500 text-xs leading-relaxed">
                    "Made my wife cry happy tears — the most beautiful
                    arrangement we've ever received."
                    <span className="text-brand-forest-400 ml-1">
                      — James T., Ascot Vale
                    </span>
                  </p> */}
                </div>

                {/* CTAs */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setModalProduct(featuredDeal);
                    }}
                    className="bg-brand-forest-500 hover:bg-brand-forest-600 text-white font-body text-sm px-8 py-3.5 rounded-full transition-all shadow-md font-semibold hover:shadow-lg hover:scale-[1.02]"
                  >
                    Order Now
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setModalProduct(featuredDeal);
                    }}
                    className="bg-transparent hover:bg-brand-cream-100 text-brand-forest-600 font-body text-sm px-6 py-3.5 rounded-full transition-colors border border-brand-forest-300 font-medium"
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {/* ── INTRO EDITORIAL ── */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-12 md:py-20 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
        <div>
          <p className="font-body text-xs text-brand-crimson-400 tracking-[0.3em] uppercase mb-3">
            Our Story
          </p>
          <h2 className="font-display text-3xl md:text-5xl text-brand-forest-700 leading-tight mb-6">
            Born from a love of
            <br />
            <em>Melbourne gardens</em>
          </h2>
          <p className="font-body text-brand-forest-700 text-lg leading-relaxed mb-8">
            Happy Day Flowers is a family-run boutique florist in Travancore,
            Melbourne. We're eco-conscious, with market fresh flowers from local suppliers, and focused on
            genuine personal service — every customer matters to us
            individually.
          </p>
          <button
            onClick={() => navigate("shop")}
            className="font-body text-sm text-brand-crimson-500 border-b border-brand-crimson-300 pb-0.5 hover:text-brand-crimson-600 hover:border-brand-crimson-400 transition-colors"
          >
            Explore the collection →
          </button>
        </div>
        <div className="relative">
          <div className="rounded-3xl overflow-hidden shadow-xl aspect-[4/5]">
            <img
              src={imgUrl(
                "https://wbaxbypftpczinlkdwvy.supabase.co/storage/v1/object/public/bouquet-images/many-reasons/1777521872188-IMG_4241.jpg",
                { width: 800, height: 1000, quality: 80 },
              )}
              alt="Happy Day Flowers studio"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="hidden sm:flex absolute -bottom-4 -left-4 bg-brand-cream-50 rounded-2xl shadow-lg px-5 py-4 items-center gap-3">
            <div className="w-10 h-10 bg-brand-cream-100 rounded-full flex items-center justify-center">
              <Heart className="w-5 h-5 text-brand-crimson-400 fill-brand-crimson-300" />
            </div>
            <div>
              <p className="font-display text-brand-forest-700 text-sm">
                Handcrafted daily
              </p>
              <p className="font-body text-xs text-brand-forest-400">
                in Travancore, Melbourne
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── SHOP BY OCCASION ── */}
      <section className="bg-brand-cream-50 py-12 md:py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <p className="font-body text-xs text-brand-crimson-400 tracking-[0.3em] uppercase text-center mb-3">
            Collections
          </p>
          <h2 className="font-display text-2xl md:text-4xl text-brand-forest-600 text-center mb-8 md:mb-12">
            Shop by Occasion
          </h2>

          {(() => {
            const usedBouquetIds = new Set();
            const resolve = (c) => {
              const match = bouquets.filter((b) =>
                b.occasions?.some((o) => o.toLowerCase() === c.category.toLowerCase()),
              );
              const from = match.length ? Math.min(...match.map((b) => b.price)) : c.fallbackFrom;
              const picked = match.find((b) => !usedBouquetIds.has(b.id));
              if (picked) usedBouquetIds.add(picked.id);
              const image = picked?.images?.[0] ?? c.fallbackImage;
              return { ...c, from, image };
            };
            const cards = collectionCards.map(resolve);
            const extras = collectionExtra.map(resolve);

            return (
              <>
                {/* Desktop: col1=big Birthday (row-span-2) | row1: Date Night, Sympathy, Just Because | row2: Get Well Soon, Thank You, Browse All */}
                <div className="hidden md:grid grid-cols-4 grid-rows-2 gap-4">
                  <ImageCard c={cards[0]} tall navigate={navigate} />
                  {cards.slice(1).map((c) => (
                    <ImageCard key={c.category} c={c} navigate={navigate} />
                  ))}
                  {extras.map((c) => (
                    <ImageCard key={c.category} c={c} navigate={navigate} />
                  ))}
                  <BrowseAllTile navigate={navigate} />
                </div>

                {/* Mobile layout */}
                <div className="md:hidden flex flex-col gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      onClick={() => navigate("shop", cards[0].category)}
                      className="group cursor-pointer relative rounded-3xl overflow-hidden shadow-sm row-span-2"
                    >
                      <img
                        src={pexelsImg(cards[0].image, { width: 600 })}
                        alt={cards[0].label}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full min-h-[300px] object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-forest-700/70 via-transparent to-transparent flex flex-col justify-end p-4">
                        <p className="font-display text-white text-sm leading-tight">{cards[0].label}</p>
                      </div>
                    </div>
                    {cards.slice(1, 3).map((c) => (
                      <div
                        key={c.category}
                        onClick={() => navigate("shop", c.category)}
                        className="group cursor-pointer relative rounded-3xl overflow-hidden shadow-sm"
                      >
                        <img src={pexelsImg(c.image, { width: 500 })} alt={c.label} loading="lazy" decoding="async" className="w-full h-36 object-cover group-hover:scale-105 transition-transform duration-700" />
                        <div className="absolute inset-0 bg-gradient-to-t from-brand-forest-700/70 via-transparent to-transparent flex flex-col justify-end p-4">
                          <p className="font-display text-white text-sm leading-tight">{c.label}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {[cards[3], extras[0]].map((c) => (
                      <div
                        key={c.category}
                        onClick={() => navigate("shop", c.category)}
                        className="group cursor-pointer relative rounded-3xl overflow-hidden shadow-sm"
                      >
                        <img src={pexelsImg(c.image, { width: 500 })} alt={c.label} loading="lazy" decoding="async" className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-700" />
                        <div className="absolute inset-0 bg-gradient-to-t from-brand-forest-700/70 via-transparent to-transparent flex flex-col justify-end p-4">
                          <p className="font-display text-white text-sm leading-tight">{c.label}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      onClick={() => navigate("shop", extras[1].category)}
                      className="cursor-pointer rounded-2xl border border-[--hdf-brown-200] bg-white flex items-center justify-between px-4 py-3 shadow-sm"
                    >
                      <div>
                        <p className="font-display text-brand-forest-700 text-sm">{extras[1].label}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-brand-crimson-400 shrink-0" />
                    </div>
                    <BrowseAllTile compact navigate={navigate} />
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-20">
        <p className="font-body text-xs text-brand-crimson-400 tracking-[0.3em] uppercase text-center mb-3">
          Process
        </p>
        <h2 className="font-display text-2xl md:text-4xl text-brand-forest-600 text-center mb-12 md:mb-20">
          From Our Florist Designers to Your Door
        </h2>

        {/* Steps — vertical on mobile, horizontal on md+ */}
        <div className="flex flex-col md:flex-row items-center md:items-start gap-0 md:gap-0">
          {process.map((p, i) => (
            <div
              key={p.step}
              className="flex flex-col md:flex-row items-center w-full md:flex-1"
            >
              {/* Step block */}
              <div className="flex flex-col items-center text-center flex-1 w-full px-4">
                {/* Flower icon */}
                <div className="relative w-24 h-24 mb-6 flex items-center justify-center">
                  <svg
                    viewBox="0 0 96 96"
                    className="absolute inset-0 w-full h-full"
                  >
                    {/* 8 petals — larger, covering the full circle area */}
                    {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
                      <ellipse
                        key={deg}
                        cx="48"
                        cy="18"
                        rx="9"
                        ry="18"
                        fill={
                          i === 0 ? "#c4354a" : i === 1 ? "#758956" : "#9e5c2a"
                        }
                        opacity={deg % 90 === 0 ? "0.7" : "0.45"}
                        transform={`rotate(${deg} 48 48)`}
                      />
                    ))}
                    {/* Centre disc */}
                    <circle
                      cx="48"
                      cy="48"
                      r="18"
                      fill={
                        i === 0 ? "#fdf2f3" : i === 1 ? "#f2f3f0" : "#fbf4ed"
                      }
                    />
                  </svg>
                  {/* Step number on top */}
                  <span
                    className="relative z-10 font-display text-xl leading-none"
                    style={{
                      color:
                        i === 0 ? "#880d1e" : i === 1 ? "#4a5138" : "#80461b",
                    }}
                  >
                    {p.step}
                  </span>
                </div>

                <h3 className="font-display text-brand-forest-700 text-lg mb-2">
                  {p.title}
                </h3>
                <p className="font-body text-brand-forest-400 text-sm leading-relaxed max-w-[220px]">
                  {p.desc}
                </p>
              </div>

              {/* Connector — dashed line: horizontal on md+, vertical on mobile */}
              {i < process.length - 1 && (
                <>
                  {/* Mobile: vertical connector */}
                  <div className="md:hidden flex flex-col items-center py-3">
                    <div className="w-px h-10 border-l-2 border-dashed border-brand-cream-300" />
                  </div>
                  {/* Desktop: horizontal connector */}
                  <div className="hidden md:flex items-center justify-center w-12 shrink-0 mt-12">
                    <div className="w-full border-t-2 border-dashed border-brand-cream-300" />
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── AWARD BANNER ── */}
      <section className="relative overflow-hidden bg-[#1a1208] py-10 md:py-14 px-4">
        {/* Subtle gold radial glow */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 50%, rgba(180,140,40,0.13) 0%, transparent 70%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto flex flex-col items-center gap-5 md:gap-7 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#b8980a]/40 bg-[#b8980a]/10">
            <Star className="w-3 h-3 fill-[#d4af37] text-[#d4af37]" />
            <span className="font-elegant text-[11px] tracking-[0.25em] uppercase text-[#d4af37]">
              Recognised by the City of Moonee Valley
            </span>
          </div>
          <img
            src={awardBannerLandscape}
            alt="Quality Business Awards 2026 — Best Florist, City of Moonee Valley VIC"
            loading="lazy"
            decoding="async"
            className="w-full max-w-2xl rounded-2xl shadow-[0_8px_48px_rgba(0,0,0,0.5)]"
          />
          <p className="font-elegant italic text-[#e8d49a] text-base md:text-lg max-w-md leading-relaxed opacity-90">
            "Quality ratings over 95% — proudly serving Melbourne's north-west
            since 2017."
          </p>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="bg-brand-cream-100 py-12 md:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <p className="font-body text-xs text-brand-crimson-400 tracking-[0.3em] uppercase text-center mb-3">
            Reviews
          </p>
          <h2 className="font-display text-2xl md:text-4xl text-brand-forest-600 text-center mb-2">
            What Melbourne Says
          </h2>
          {/* Overall rating badge */}
          {reviewsMeta.rating && (
            <div className="flex items-center justify-center gap-2 mb-10 md:mb-14">
              <div className="flex">
                {[...Array(5)].map((_, j) => (
                  <Star
                    key={j}
                    className="w-4 h-4 fill-brand-crimson-300 text-brand-crimson-300"
                  />
                ))}
              </div>
              <span className="font-display text-brand-forest-600 text-lg leading-none">
                {reviewsMeta.rating}
              </span>
              {reviewsMeta.total_count && (
                <span className="font-body text-xs text-brand-forest-400">
                  ({reviewsMeta.total_count} reviews on Google)
                </span>
              )}
            </div>
          )}
          {!reviewsMeta.rating && <div className="mb-10 md:mb-14" />}

          {/* Loading shimmer */}
          {reviewsLoading ? (
            <div className="hidden md:grid grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="bg-brand-cream-50 rounded-3xl p-6 space-y-3 animate-pulse"
                >
                  <div className="h-3 w-20 bg-brand-cream-200 rounded-full" />
                  <div className="h-3 w-full bg-brand-cream-200 rounded-full" />
                  <div className="h-3 w-4/5 bg-brand-cream-200 rounded-full" />
                  <div className="h-3 w-1/2 bg-brand-cream-200 rounded-full mt-4" />
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Desktop grid — 5 reviews + 1 CTA card */}
              <div className="hidden md:grid grid-cols-3 gap-6 mb-8">
                {reviews.slice(0, 5).map((r, i) => (
                  <div
                    key={i}
                    className="bg-brand-cream-50 rounded-3xl p-6 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col"
                  >
                    <div className="flex items-center gap-2.5 mb-4">
                      {r.avatar ? (
                        <img
                          src={r.avatar}
                          alt={r.author}
                          loading="lazy"
                          decoding="async"
                          className="w-8 h-8 rounded-full object-cover shrink-0"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-brand-crimson-100 flex items-center justify-center shrink-0">
                          <span className="font-body text-xs font-bold text-brand-crimson-400">
                            {r.author[0]}
                          </span>
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-body text-xs font-semibold text-brand-forest-700 truncate">
                          {r.author}
                        </p>
                        {r.relative && (
                          <p className="font-body text-[10px] text-brand-forest-300">
                            {r.relative}
                          </p>
                        )}
                      </div>
                      <div className="ml-auto shrink-0">
                        <svg
                          viewBox="0 0 24 24"
                          className="w-4 h-4"
                          fill="none"
                        >
                          <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            fill="#4285F4"
                          />
                          <path
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            fill="#34A853"
                          />
                          <path
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                            fill="#FBBC05"
                          />
                          <path
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            fill="#EA4335"
                          />
                        </svg>
                      </div>
                    </div>
                    <div className="flex mb-3">
                      {[...Array(r.rating ?? 5)].map((_, j) => (
                        <Star
                          key={j}
                          className="w-3 h-3 fill-brand-crimson-200 text-brand-crimson-300"
                        />
                      ))}
                    </div>
                    <p className="font-body text-brand-forest-700 text-base leading-relaxed flex-1">
                      "
                      {r.text.length > 180
                        ? r.text.slice(0, 180).trimEnd() + "…"
                        : r.text}
                      "
                    </p>
                  </div>
                ))}

                {/* CTA card — 6th slot */}
                <a
                  href="https://www.google.com/search?q=happy+day+flowers&sourceid=chrome&ie=UTF-8#lrd=0x6ad65d329cf37b77:0xab2f377bfbe3623d,1,,,,"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-brand-forest-500 rounded-3xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col items-center justify-center text-center gap-4 group"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="w-8 h-8 shrink-0"
                    fill="none"
                  >
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  <div>
                    <p className="font-display text-5xl text-white leading-none mb-1">
                      {reviewsMeta.rating ?? "5.0"}
                    </p>
                    <div className="flex justify-center gap-0.5 mb-2">
                      {[...Array(5)].map((_, j) => (
                        <Star
                          key={j}
                          className="w-4 h-4 fill-yellow-400 text-yellow-400"
                        />
                      ))}
                    </div>
                    {reviewsMeta.total_count && (
                      <p className="font-body text-xs text-white/70">
                        {reviewsMeta.total_count} reviews on Google
                      </p>
                    )}
                  </div>
                  <span className="mt-2 inline-flex items-center gap-1.5 bg-white text-brand-forest-600 font-body text-xs font-bold px-4 py-2 rounded-full group-hover:bg-brand-cream-50 transition-colors">
                    Write a review ✦
                  </span>
                </a>
              </div>

              {/* Mobile carousel — reviews only, CTA below */}
              {reviews.length > 0 && (
                <div className="md:hidden text-center">
                  <div className="bg-brand-cream-50 rounded-3xl p-7 shadow-sm mx-auto max-w-sm">
                    <div className="flex items-center gap-2.5 mb-4 text-left">
                      {reviews[testi.index]?.avatar ? (
                        <img
                          src={reviews[testi.index].avatar}
                          alt={reviews[testi.index].author}
                          className="w-9 h-9 rounded-full object-cover shrink-0"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-brand-crimson-100 flex items-center justify-center shrink-0">
                          <span className="font-body text-sm font-bold text-brand-crimson-400">
                            {reviews[testi.index]?.author?.[0]}
                          </span>
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-body text-xs font-semibold text-brand-forest-700 truncate">
                          {reviews[testi.index]?.author}
                        </p>
                        {reviews[testi.index]?.relative && (
                          <p className="font-body text-[10px] text-brand-forest-300">
                            {reviews[testi.index].relative}
                          </p>
                        )}
                      </div>
                      <svg
                        viewBox="0 0 24 24"
                        className="w-4 h-4 shrink-0"
                        fill="none"
                      >
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                      </svg>
                    </div>
                    <div className="flex justify-center mb-3">
                      {[...Array(reviews[testi.index]?.rating ?? 5)].map(
                        (_, j) => (
                          <Star
                            key={j}
                            className="w-4 h-4 fill-brand-crimson-200 text-brand-crimson-300"
                          />
                        ),
                      )}
                    </div>
                    <p className="font-body text-lg text-brand-forest-700 leading-relaxed">
                      "{reviews[testi.index]?.text?.slice(0, 200) ?? ""}
                      {(reviews[testi.index]?.text?.length ?? 0) > 200
                        ? "…"
                        : ""}
                      "
                    </p>
                  </div>
                  <div className="flex justify-center gap-2 mt-6 mb-6">
                    {reviews.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => testi.goTo(i)}
                        className={`h-1.5 rounded-full transition-all duration-300 ${i === testi.index ? "bg-brand-crimson-500 w-6" : "bg-brand-crimson-100 w-2"}`}
                      />
                    ))}
                  </div>
                  {/* Mobile CTA */}
                  <a
                    href="https://www.google.com/search?q=happy+day+flowers&sourceid=chrome&ie=UTF-8#lrd=0x6ad65d329cf37b77:0xab2f377bfbe3623d,1,,,,"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-brand-forest-500 hover:bg-brand-forest-600 text-white font-body text-sm font-semibold px-6 py-3.5 rounded-full transition-colors shadow-md mx-auto"
                  >
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, j) => (
                        <Star
                          key={j}
                          className="w-3 h-3 fill-yellow-400 text-yellow-400"
                        />
                      ))}
                    </div>
                    Write a Google review
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── REFERRAL ── */}
      <section className="relative overflow-hidden py-16 md:py-24 px-4 bg-brand-crimson-50">
        {/* Decorative petals — soft pink + forest tints */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-6 -left-6 w-40 h-40 rounded-full opacity-30 bg-brand-crimson-100" />
          <div className="absolute top-1/2 -right-10 w-56 h-56 rounded-full opacity-30 bg-brand-forest-50" />
          <div className="absolute -bottom-8 left-1/3 w-32 h-32 rounded-full opacity-40 bg-brand-crimson-100" />
        </div>

        <div className="relative max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 border bg-brand-crimson-100 border-brand-crimson-200">
              <Gift className="w-3.5 h-3.5 text-brand-crimson-500" />
              <span className="font-body text-[11px] font-semibold tracking-widest uppercase text-brand-crimson-600">
                Referral Rewards
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl text-brand-forest-700 leading-tight mb-4">
              Share the joy,
              <br />
              <em>earn rewards</em>
            </h2>
            <p className="font-body text-sm text-brand-forest-400 max-w-md mx-auto leading-relaxed">
              Love Happy Day Flowers? Share your unique referral link with
              friends — when they place their first order, you both get
              rewarded.
            </p>
          </div>

          {/* Steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-12">
            {[
              {
                icon: <Users className="w-5 h-5" />,
                step: "01",
                title: "Share your code",
                desc: "Every account gets a unique referral code. Share it with friends, family, or anyone who loves flowers.",
              },
              {
                icon: <Heart className="w-5 h-5" />,
                step: "02",
                title: "They place an order",
                desc: "Your friend signs up and places their first order using your referral code at checkout.",
              },
              {
                icon: <Coins className="w-5 h-5" />,
                step: "03",
                title: "You both get points",
                desc: "You earn bonus Happy Day Points the moment their order is confirmed. They get a welcome reward too.",
              },
            ].map((item) => (
              <div
                key={item.step}
                className="relative rounded-3xl p-6 border bg-white border-brand-forest-100 flex flex-col gap-4 shadow-sm"
              >
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-sm bg-brand-forest-50 text-brand-forest-600">
                    {item.icon}
                  </div>
                  <span className="font-display text-4xl leading-none font-bold opacity-20 text-brand-crimson-500">
                    {item.step}
                  </span>
                </div>
                <div>
                  <p className="font-body text-sm font-bold text-brand-forest-700 mb-1">
                    {item.title}
                  </p>
                  <p className="font-body text-xs text-brand-forest-400 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Reward callout */}
          <div className="rounded-3xl border p-6 md:p-8 flex flex-col md:flex-row items-center gap-6 md:gap-10 mb-10 bg-white border-brand-forest-100 shadow-sm">
            <div className="flex-1 text-center md:text-left">
              <p className="font-body text-[11px] font-semibold tracking-widest uppercase mb-2 text-brand-crimson-500">
                Your reward
              </p>
              <p className="font-display text-4xl md:text-5xl text-brand-crimson-500 leading-none mb-2">
                {POINTS_REFERRAL} <span className="text-2xl">pts</span>
              </p>
              <p className="font-body text-xs text-brand-forest-400 leading-relaxed max-w-xs mx-auto md:mx-0">
                Earned instantly when your friend's first order is confirmed.
                Points convert to dollars off your next order.
              </p>
            </div>
            <div className="w-px h-16 bg-brand-forest-100 hidden md:block" />
            <div className="flex-1 text-center md:text-left">
              <p className="font-body text-[11px] font-semibold tracking-widest uppercase mb-2 text-brand-forest-500">
                Their reward
              </p>
              <p className="font-display text-4xl md:text-5xl text-brand-forest-500 leading-none mb-2">
                {POINTS_SIGNUP_BONUS} <span className="text-2xl">pts</span>
              </p>
              <p className="font-body text-xs text-brand-forest-400 leading-relaxed max-w-xs mx-auto md:mx-0">
                Your friend gets welcome points just for joining — a little gift
                to start their Happy Day journey.
              </p>
            </div>
            <div className="w-px h-16 bg-brand-forest-100 hidden md:block" />
            <div className="flex-1 text-center">
              <p className="font-body text-[11px] font-semibold tracking-widest uppercase mb-2 text-brand-forest-500">
                Points value
              </p>
              <p className="font-display text-4xl md:text-5xl text-brand-crimson-500 leading-none mb-2 flex items-center justify-center gap-0">
                <svg viewBox="0 0 24 24" className="w-8 h-8 md:w-10 md:h-10 inline-block" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="22"/><path d="M17 6.5C17 6.5 15.5 4 12 4C8.5 4 7 6 7 8C7 10 8.5 11 12 12C15.5 13 17 14.5 17 16.5C17 18.5 15.5 20 12 20C8.5 20 7 17.5 7 17.5"/></svg><span>1</span> <span className="text-xl opacity-60">/ {POINTS_PER_DOLLAR} pts</span>
              </p>
              <p className="font-body text-xs text-brand-forest-400 leading-relaxed max-w-xs mx-auto">
                Redeem points at checkout for real dollars off. No minimum, no
                fuss — just flowers.
              </p>
            </div>
          </div>

          {/* CTA */}
          {user ? (
            <div className="text-center">
              <p className="font-body text-xs text-brand-forest-400 mb-4">
                Share your code and start earning — every referral counts.
              </p>
              {profile?.referral_code ? (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(profile.referral_code);
                    setReferralCopied(true);
                    setTimeout(() => setReferralCopied(false), 2500);
                  }}
                  className={`inline-flex items-center gap-3 font-body font-semibold px-6 py-3.5 rounded-full transition-all shadow-md hover:shadow-lg text-white ${
                    referralCopied
                      ? "bg-brand-forest-500 hover:bg-brand-forest-600"
                      : "bg-brand-crimson-500 hover:bg-brand-crimson-600"
                  }`}
                >
                  {referralCopied ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span className="text-sm">Copied!</span>
                    </>
                  ) : (
                    <>
                      <span className="text-xs tracking-widest uppercase opacity-70">
                        Your code
                      </span>
                      <span className="text-base tracking-[0.2em] uppercase">
                        {profile.referral_code}
                      </span>
                      <span className="text-xs opacity-60 border-l border-white/30 pl-3">
                        Copy
                      </span>
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={() => setAccountPanelOpen(true)}
                  className="inline-flex items-center gap-2 font-body text-sm font-semibold px-8 py-3.5 rounded-full transition-all shadow-md hover:shadow-lg bg-brand-crimson-500 hover:bg-brand-crimson-600 text-white"
                >
                  View my referral code
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          ) : (
            <div className="text-center">
              <p className="font-body text-xs text-brand-forest-400 mb-4">
                Create a free account to get your unique referral code and start
                earning.
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <button
                  onClick={() => setAuthModalOpen(true)}
                  className="inline-flex items-center gap-2 font-body text-sm font-semibold px-8 py-3.5 rounded-full transition-all shadow-md hover:shadow-lg bg-brand-crimson-500 hover:bg-brand-crimson-600 text-white"
                >
                  <Sparkles className="w-4 h-4" />
                  Sign up — it's free
                </button>
                <button
                  onClick={() => setAuthModalOpen(true)}
                  className="inline-flex items-center gap-2 font-body text-sm px-6 py-3.5 rounded-full border transition-all border-brand-forest-300 text-brand-forest-600 hover:bg-brand-forest-50 bg-transparent"
                >
                  Already have an account? Sign in
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="bg-brand-cream-100 border-t border-brand-cream-300 py-12 md:py-20 px-4 text-center">
        <p className="font-body text-xs text-brand-crimson-400 tracking-[0.3em] uppercase mb-4">
          Ready to brighten someone's day?
        </p>
        <h2 className="font-display text-2xl md:text-5xl text-brand-forest-700 mb-5 max-w-xl mx-auto leading-tight">
          Every day is a good day for <em>flowers</em>
        </h2>
        <p className="font-body text-brand-forest-400 text-sm mb-8 max-w-md mx-auto">
          Beautifully arranged, same-day delivered, and made with love in
          Melbourne's own Travancore.
        </p>
        <button
          onClick={() => navigate("shop")}
          className="bg-brand-crimson-500 hover:bg-brand-crimson-600 text-white font-body px-12 py-4 rounded-full text-sm tracking-wider transition-colors shadow-lg hover:shadow-xl"
        >
          Shop the Full Collection
        </button>
      </section>
    </div>
  );
}
